import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Plus, LogOut, Download, Clock } from "lucide-react";

const opt = (j) => `${j.size} · ${j.color === "color" ? "Colour" : "Mono"} · ${j.paper}gsm · ${j.sided}-sided`;

export default function CustomerDashboard() {
  const nav = useNavigate();
  const { user, signOut } = useAuth();
  const [jobs, setJobs] = useState(null);

  useEffect(() => {
    api.get("/jobs/mine").then(({ data }) => setJobs(data.jobs)).catch(() => setJobs([]));
  }, []);

  const download = (id) => {
    const token = localStorage.getItem("bag_token");
    window.open(`${API}/jobs/${id}/download?auth=${token}`, "_blank");
  };

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-500 hidden sm:block">{user?.email}</span>
            <button onClick={() => { signOut(); nav("/"); }} data-testid="logout-btn" className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-ink">
              <LogOut size={16} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="uppercase text-xs tracking-[0.2em] font-medium text-brand">Your account</span>
            <h1 className="font-heading font-black text-3xl sm:text-4xl text-ink mt-2">My print requests</h1>
          </div>
          <button onClick={() => nav("/submit")} data-testid="new-job-btn" className="inline-flex items-center gap-2 bg-brand hover:bg-brand-hover text-white font-semibold px-5 py-3 rounded-lg transition-colors">
            <Plus size={18} /> New request
          </button>
        </div>

        <div className="mt-8">
          {jobs === null ? (
            <div className="grid place-items-center py-20"><Loader2 className="animate-spin text-brand" size={30} /></div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="no-jobs">
              <Clock className="mx-auto text-slate-300" size={40} />
              <p className="font-semibold text-ink mt-4">No print requests yet</p>
              <p className="text-slate-500 text-sm mt-1">Submit your first job to see it here.</p>
            </div>
          ) : (
            <div className="grid gap-4" data-testid="jobs-list">
              {jobs.map((j) => (
                <div key={j.id} className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6 shadow-sm flex flex-wrap items-center justify-between gap-4 hover:-translate-y-0.5 transition-transform">
                  <div className="min-w-0">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-brand font-semibold">{j.job_number}</span>
                      <StatusBadge status={j.status} />
                    </div>
                    <h3 className="font-heading font-bold text-lg text-ink mt-1.5 truncate">{j.title}</h3>
                    <p className="text-sm text-slate-500 mt-0.5">{opt(j)} · {j.copies} {j.copies > 1 ? "copies" : "copy"}</p>
                    <p className="text-xs text-slate-400 mt-1">Submitted {new Date(j.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <div className="font-heading font-extrabold text-xl text-ink">€{Number(j.price).toFixed(2)}</div>
                      <div className="text-xs text-slate-400">{j.pickup_asap ? "Pick-up ASAP" : `Pick-up ${j.pickup_datetime ? new Date(j.pickup_datetime).toLocaleString() : "scheduled"}`}</div>
                    </div>
                    {j.storage_path && (
                      <button onClick={() => download(j.id)} data-testid={`download-${j.id}`} className="grid place-items-center w-10 h-10 rounded-lg border border-slate-200 text-slate-600 hover:border-brand hover:text-brand transition-colors" title="Download file">
                        <Download size={17} />
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
